import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { scrollToSection, scrollToTop } from '../utils/helpers';
import { stickyNavigation, mobileMenuAnimation, navLinkHover, iconFeedback, navButtonRippleEffect } from '../utils/animations';
import logo from '../images/logo/logo.jpeg';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const logoRef = useRef(null);

  // Track scroll position to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const sections = ['home', 'about', 'contact'];
        const scrollPosition = window.scrollY + 100;

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i]);
          if (section && section.offsetTop <= scrollPosition) {
            setActiveSection(sections[i]);
            break;
          }
        }

        // Special case for when at the very top
        if (window.scrollY < 100) {
          setActiveSection('home');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Update active section based on current route
  useEffect(() => {
    if (location.pathname === '/') {
      setActiveSection('home');
    } else if (location.pathname === '/gallery') {
      setActiveSection('gallery');
      // Scroll to top when navigating to gallery page
      scrollToTop();
    }
  }, [location.pathname]);

  // Initialize simple animations
  useEffect(() => {
    // Simple sticky navigation
    if (navRef.current) {
      const handleScroll = () => {
        const scrolled = window.scrollY > 50;
        if (navRef.current) {
          navRef.current.style.backgroundColor = scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)';
          navRef.current.style.boxShadow = scrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.1)';
        }
      };
      
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Simple mobile menu animation
  useEffect(() => {
    if (mobileMenuRef.current) {
      if (isMenuOpen) {
        mobileMenuRef.current.style.display = 'block';
        mobileMenuRef.current.style.opacity = '0';
        setTimeout(() => {
          if (mobileMenuRef.current) {
            mobileMenuRef.current.style.opacity = '1';
          }
        }, 10);
      } else {
        if (mobileMenuRef.current) {
          mobileMenuRef.current.style.opacity = '0';
          setTimeout(() => {
            if (mobileMenuRef.current) {
              mobileMenuRef.current.style.display = 'none';
            }
          }, 200);
        }
      }
    }
  }, [isMenuOpen]);

  const handleScrollToSection = (sectionId) => {
    scrollToSection(sectionId, navigate, location.pathname);
    setActiveSection(sectionId);
    setIsMenuOpen(false);
  };

  const navigateToGallery = () => {
    navigate('/gallery');
    setActiveSection('gallery');
    setIsMenuOpen(false);
    scrollToTop();
  };

  const navigateToHome = () => {
    navigate('/');
    setActiveSection('home');
    scrollToTop();
    setIsMenuOpen(false);
  };

  const getButtonClasses = (section) => {
    const baseClasses = "px-4 py-2 text-sm font-medium transition-all duration-200 rounded-md relative";
    const activeClasses = "text-orange-600 bg-orange-50 shadow-sm";
    const inactiveClasses = "text-gray-700 hover:text-orange-600 hover:bg-orange-50";
    
    return `${baseClasses} ${activeSection === section ? activeClasses : inactiveClasses}`;
  };

  const getMobileButtonClasses = (section) => {
    const baseClasses = "block px-4 py-3 text-base font-medium w-full text-left transition-colors duration-200 rounded-md";
    const activeClasses = "text-orange-600 bg-orange-50";
    const inactiveClasses = "text-gray-700 hover:text-orange-600 hover:bg-gray-50";
    
    return `${baseClasses} ${activeSection === section ? activeClasses : inactiveClasses}`;
  };

  return (
    <nav ref={navRef} className="sticky top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md border-b border-gray-100 z-40 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <button 
              ref={logoRef}
              onClick={() => {
                navigate('/');
                scrollToTop();
                setActiveSection('home');
                setIsMenuOpen(false);
              }}
              className="flex items-center space-x-3 group transition-colors duration-200"
            >
              {/* Clean Logo Container */}
              <div className="logo-container w-10 h-10 rounded-lg overflow-hidden shadow-sm border border-gray-200 group-hover:border-orange-300 transition-all duration-200">
                <img 
                  src={logo} 
                  alt="RamTirathArt Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Clean Brand Text */}
              <div>
                <span className="text-xl font-bold text-gray-800 group-hover:text-orange-600 transition-colors duration-200 hidden sm:block">
                  RamTirathArt
                </span>
                <span className="text-lg font-bold text-gray-800 group-hover:text-orange-600 transition-colors duration-200 block sm:hidden">
                  RTA
                </span>
              </div>
            </button>
          </div>
          
          {/* Clean Desktop menu */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-1">
              <button
                onClick={navigateToHome}
                className={getButtonClasses('home')}
              >
                Home
              </button>
              <button
                onClick={() => handleScrollToSection('about')}
                className={getButtonClasses('about')}
              >
                About
              </button>
              <button
                onClick={navigateToGallery}
                className={getButtonClasses('gallery')}
              >
                Gallery
              </button>
              <button
                onClick={() => handleScrollToSection('contact')}
                className={getButtonClasses('contact')}
              >
                Contact
              </button>
            </div>
          </div>
          
          {/* Simple Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:text-orange-600 hover:bg-gray-50 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Simple Mobile menu */}
        {isMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              <button
                onClick={navigateToHome}
                className={getMobileButtonClasses('home')}
              >
                Home
              </button>
              <button
                onClick={() => handleScrollToSection('about')}
                className={getMobileButtonClasses('about')}
              >
                About
              </button>
              <button
                onClick={navigateToGallery}
                className={getMobileButtonClasses('gallery')}
              >
                Gallery
              </button>
              <button
                onClick={() => handleScrollToSection('contact')}
                className={getMobileButtonClasses('contact')}
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
